import { type KeyboardEvent, useRef, useState } from "react";
import { Form, Formik, type FormikErrors } from "formik";
import isEqual from "lodash/isEqual";
import { Calendar01Icon } from "vega-icons";

import { type ErrorType } from "@vega-api/configs/customClientQuery";
import {
  useCancelDealShortlistTeaserEmailScheduleV2,
  useCreateDealShortlistTeaserEmailV2,
  useScheduleDealShortlistTeaserEmailV2,
  useSendDealShortlistTeaserEmailsV2,
  useUpdateDealTeaserEmailV2,
} from "@vega-api/generated/coinvest-deal-teaser-email-v2/coinvest-deal-teaser-email-v2";
import { type DealPermissionUser } from "@vega-api/generated/types/dealPermissionUser";
import { type TeaserEmail } from "@vega-api/generated/types/teaserEmail";
import { type TeaserEmailContact } from "@vega-api/generated/types/teaserEmailContact";
import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import { ThemedDateField } from "@vega-core/components/Form/Controls/DateField/ThemedDateField";
import {
  getUserOrFreeformEmailItems,
  type SearchMultiSelectItemWithUser,
  SearchUserMultiselect,
} from "@vega-core/components/Form/Controls/Input/SearchUser/SearchUserMultiselect";
import { InputField } from "@vega-core/components/Form/Fields/InputField";
import { ValidationErrorText } from "@vega-core/components/Form/Layout/ValidationError";
import { Notice, NoticeType } from "@vega-core/components/Notice/Notice";
import { useToast } from "@vega-core/components/Toast/useToast";
import { useBoolean } from "@vega-core/hooks/useBoolean";
import { m } from "@vega-core/paraglide/messages";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";
import { htmlToPlainText } from "@vega-core/utils/richText";
import { useCoInvestErrorToast } from "@vega-modules/hooks/useCoInvestErrorToast";

import { MIN_SEARCH_QUERY_LENGTH } from "../../constants/searchConstants";
import { DraftAutoSave } from "../SendDrafts/DraftAutoSave";

import { DraftEmailEditor } from "./DraftEmailEditor";
import {
  buildAccessRows,
  buildDraftUpdatePayload,
  composeScheduledAt,
  type DraftEmailFormValues,
  resolveGrant,
} from "./draftEmailUtils";
import { EmailAccessSection } from "./EmailAccessSection";
import { PreviewEmailModal } from "./PreviewEmailModal";
import { ScheduledTimeInput } from "./ScheduledTimeInput";
import { useRecipientAccess } from "./useRecipientAccess";

type SubmitMode = "draft" | "schedule" | "send";

const successToastTitle = (mode: SubmitMode): string => {
  if (mode === "schedule") return m.coInvestments_draftEmail_toast_scheduled();

  if (mode === "send") return m.coInvestments_draftEmail_toast_sent();

  return m.coInvestments_draftEmail_toast_saved();
};

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [contenteditable="true"], [tabindex]:not([tabindex="-1"])';

// HeadlessUI's focus-trap wraps a forward Tab off the last control back to the
// Lexical body instead of the modal top: the contenteditable is the only element
// that updates document.activeElement synchronously inside the trap's focus-guard
// dispatch, so the trap settles on it. Redirect the forward wrap to the first
// focusable (the close button) ourselves. Backwards / Shift+Tab is unaffected.
const wrapTabToModalTop = (event: KeyboardEvent<HTMLFormElement>) => {
  if (event.key !== "Tab" || event.shiftKey) return;

  const dialog = event.currentTarget.closest<HTMLElement>('[role="dialog"]');

  if (!dialog) return;

  const focusable = dialog.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);

  if (focusable.length === 0) return;

  if (document.activeElement !== focusable[focusable.length - 1]) return;

  event.preventDefault();
  focusable[0].focus();
};

type DraftEmailFormProps = {
  dealId: string;
  limitedPartnerId: string;
  dealShortlistItemId: string;
  /** Present when editing an existing draft (update); absent creates a new one. */
  emailId?: string;
  isScheduled?: boolean;
  /** Required to create a draft; unused when updating. */
  sender?: TeaserEmailContact;
  accessUsers: DealPermissionUser[];
  seedUserIds?: string[];
  initialValues: DraftEmailFormValues;
  onSaved: () => void;
  onScheduleCancelled: (email: TeaserEmail) => void;
  onCancel: () => void;
  autoSave?: boolean;
  readOnly?: boolean;
};

export function DraftEmailForm({
  dealId,
  limitedPartnerId,
  dealShortlistItemId,
  emailId,
  isScheduled = false,
  sender,
  accessUsers,
  seedUserIds = [],
  initialValues,
  onSaved,
  onScheduleCancelled,
  onCancel,
  autoSave = false,
  readOnly = false,
}: DraftEmailFormProps) {
  const showToast = useToast();
  const showErrorToast = useCoInvestErrorToast();
  const preview = useBoolean(false);
  const [toSearchValue, setToSearchValue] = useState("");
  const [ccSearchValue, setCcSearchValue] = useState("");
  const [submittingMode, setSubmittingMode] = useState<SubmitMode | null>(null);
  const submitModeRef = useRef<SubmitMode>("draft");

  const {
    poolItems,
    knownUserIds,
    activeAccessRoleById,
    defaultRoleId,
    roleOptions,
    getRoleById,
    getRoleByName,
    isRecipientSearchPending,
    setRecipientSearch,
  } = useRecipientAccess({
    accessUsers,
    dealId,
    limitedPartnerId,
    seedUserIds,
  });

  const onMutationError = (error: ErrorType<unknown>) => showErrorToast(error);

  const { mutateAsync: createDraft } = useCreateDealShortlistTeaserEmailV2({
    mutation: { onError: onMutationError },
  });

  const { mutateAsync: updateDraft } = useUpdateDealTeaserEmailV2({
    mutation: { onError: onMutationError },
  });

  const { mutateAsync: scheduleEmail } = useScheduleDealShortlistTeaserEmailV2({
    mutation: { onError: onMutationError },
  });

  const { mutateAsync: sendEmail } = useSendDealShortlistTeaserEmailsV2({
    mutation: { onError: onMutationError },
  });

  const { mutateAsync: cancelSchedule, isPending: isCancellingSchedule } =
    useCancelDealShortlistTeaserEmailScheduleV2({
      mutation: { onError: onMutationError },
    });

  const validate = (values: DraftEmailFormValues) => {
    const errors: FormikErrors<DraftEmailFormValues> = {};

    if (!values.subject.trim()) {
      errors.subject = m.coInvestments_editEmail_validation_subjectRequired();
    }

    if (!htmlToPlainText(values.body)) {
      errors.body = m.coInvestments_editEmail_validation_required();
    }

    if (submitModeRef.current === "schedule") {
      if (values.to.length === 0) {
        errors.to =
          m.coInvestments_draftEmail_validation_toRequiredToSchedule();
      }

      const scheduledAt = composeScheduledAt(
        values.scheduleDate,
        values.scheduleTime,
      );

      if (!scheduledAt) {
        errors.scheduleDate =
          m.coInvestments_draftEmail_validation_scheduleRequired();
      } else if (new Date(scheduledAt).getTime() <= Date.now()) {
        errors.scheduleDate =
          m.coInvestments_draftEmail_validation_scheduleInPast();
      }
    }

    return errors;
  };

  // Awaited end-to-end so Formik keeps isSubmitting set across the whole chain.
  // A SCHEDULED email is edit-locked (Gmail-style): if the user unschedules it,
  // it returns to DRAFT before this runs, so no unschedule step is needed here.
  const handleSubmit = async (values: DraftEmailFormValues) => {
    const mode = submitModeRef.current;
    setSubmittingMode(mode);
    const content = buildDraftUpdatePayload({
      values,
      knownUserIds,
      defaultRoleId,
    });
    const scheduledAt = composeScheduledAt(
      values.scheduleDate,
      values.scheduleTime,
    );

    // Compare mapped payloads, not raw form items: freeform CC entries get a
    // fresh synthetic id each render, so raw items always read as dirty.
    const isContentDirty = !isEqual(
      content,
      buildDraftUpdatePayload({
        values: initialValues,
        knownUserIds,
        defaultRoleId,
      }),
    );

    try {
      let targetEmailId = emailId;

      if (targetEmailId) {
        if (isContentDirty) {
          await updateDraft({ dealId, emailId: targetEmailId, data: content });
        }
      } else if (sender) {
        const created = await createDraft({
          dealId,
          data: { dealShortlistItemId, sender, ...content },
        });
        targetEmailId = created.data.id;
      }

      if (mode === "schedule" && targetEmailId && scheduledAt) {
        await scheduleEmail({
          dealId,
          emailId: targetEmailId,
          data: { scheduledAt },
        });
      }

      if (mode === "send" && targetEmailId) {
        await sendEmail({ dealId, data: { emailIds: [targetEmailId] } });
      }

      showToast({
        variant: "success",
        title: successToastTitle(mode),
      });
      onSaved();
    } catch {
      // onError shows the toast; swallow so Formik settles isSubmitting.
    } finally {
      setSubmittingMode(null);
    }
  };

  // Unscheduling an edit-locked email returns it to DRAFT. The modal stays open
  // and refetches the email so it re-renders editable in place.
  const handleCancelSchedule = async () => {
    if (!emailId) return;

    try {
      const cancelled = await cancelSchedule({ dealId, emailId });
      showToast({
        variant: "success",
        title: m.coInvestments_draftEmail_toast_scheduleCancelled(),
      });
      onScheduleCancelled(cancelled.data);
    } catch {
      // onError shows the toast.
    }
  };

  return (
    <Formik
      enableReinitialize={!autoSave}
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validate={validate}
    >
      {({ values, errors, isSubmitting, setFieldValue, submitForm }) => {
        const toItems = getUserOrFreeformEmailItems({
          users: poolItems,
          selectedItems: values.to,
          searchValue: toSearchValue,
        });

        const ccItems = getUserOrFreeformEmailItems({
          users: poolItems,
          selectedItems: values.cc,
          searchValue: ccSearchValue,
        });

        const { accessGrants, alreadyHasAccess, nonUsers } = buildAccessRows({
          values,
          knownUserIds,
          activeAccessRoleById,
          defaultRoleId,
          getRoleName: (roleId) => getRoleById(roleId)?.name,
        });

        const onToggleGrant = (userId: string, on: boolean) => {
          setFieldValue("grants", {
            ...values.grants,
            [userId]: {
              ...resolveGrant(values.grants, userId, defaultRoleId),
              on,
            },
          });
        };

        const onChangeGrantRole = (userId: string, roleName: string) => {
          const roleId = getRoleByName(roleName)?.id;

          if (!roleId) return;

          setFieldValue("grants", {
            ...values.grants,
            [userId]: { on: values.grants[userId]?.on ?? true, roleId },
          });
        };

        const onSelectRecipients = (
          field: "cc" | "to",
          newItems: SearchMultiSelectItemWithUser[],
        ) => {
          const previousIds = new Set(values[field].map((item) => item.id));
          const addedGrantableItems = newItems.filter(
            (item) =>
              !previousIds.has(item.id) &&
              knownUserIds.has(item.id) &&
              !activeAccessRoleById.has(item.id),
          );

          if (addedGrantableItems.length > 0) {
            const nextGrants = { ...values.grants };
            addedGrantableItems.forEach((item) => {
              nextGrants[item.id] = {
                ...resolveGrant(values.grants, item.id, defaultRoleId),
                on: true,
              };
            });
            setFieldValue("grants", nextGrants);
          }

          setFieldValue(field, newItems);
        };

        const triggerSubmit = (mode: SubmitMode) => {
          submitModeRef.current = mode;
          submitForm();
        };

        const hasBody = !!htmlToPlainText(values.body);

        const isSendable =
          values.to.length > 0 && !!values.subject.trim() && hasBody;

        const clearSchedule = () => {
          setFieldValue("scheduleDate", undefined);
          setFieldValue("scheduleTime", "");
        };

        const hasSchedule = !!values.scheduleDate || !!values.scheduleTime;

        return (
          <Form onKeyDown={wrapTabToModalTop}>
            <div className="flex flex-col gap-4 items-stretch text-start mx-auto sm:min-w-[36rem]">
              <div
                className={cnTwMerge(
                  "flex flex-col gap-4",
                  isScheduled && "opacity-60",
                )}
                inert={isScheduled}
              >
                <SearchUserMultiselect
                  // autoHighlight="always" is supported by base-ui at runtime but
                  // missing from its 1.1.0 types; patched locally via
                  // .yarn/patches/@base-ui-react-npm-1.1.0-*.patch until the upstream
                  // fix lands — see https://github.com/mui/base-ui/issues/4188
                  autoHighlight="always"
                  className="bg-background/primary"
                  disableSelectedOptions
                  disabled={readOnly}
                  error={errors.to as string}
                  isLoading={isRecipientSearchPending}
                  items={toItems}
                  leftLabel={m.coInvestments_editEmail_emailSentTo_label()}
                  leftLabelClassName="text-text/secondary"
                  minSearchLength={MIN_SEARCH_QUERY_LENGTH}
                  multiple
                  name="to"
                  onSearchChange={(value) => {
                    setToSearchValue(value);
                    setRecipientSearch(value);
                  }}
                  onSelectionChange={(newUsers) => {
                    onSelectRecipients("to", newUsers);
                    setToSearchValue("");
                  }}
                  selectedValues={values.to}
                />
                <SearchUserMultiselect
                  autoHighlight="always"
                  className="bg-background/primary"
                  disableSelectedOptions
                  disabled={readOnly}
                  isLoading={isRecipientSearchPending}
                  items={ccItems}
                  leftLabel={m.coInvestments_editEmail_cc_label()}
                  leftLabelClassName="text-text/secondary"
                  minSearchLength={MIN_SEARCH_QUERY_LENGTH}
                  multiple
                  name="cc"
                  onSearchChange={(value) => {
                    setCcSearchValue(value);
                    setRecipientSearch(value);
                  }}
                  onSelectionChange={(newUsers) => {
                    onSelectRecipients("cc", newUsers);
                    setCcSearchValue("");
                  }}
                  selectedValues={values.cc}
                />
                <InputField
                  disabled={readOnly}
                  inputClassName="body-base-medium"
                  leftLabel={m.coInvestments_editEmail_subjectLabel()}
                  leftLabelClassName="text-text/secondary"
                  name="subject"
                  placeholder={m.coInvestments_editEmail_subjectPlaceholder()}
                  withBorder
                />
                <div className="flex flex-col gap-1">
                  <DraftEmailEditor
                    dataTestId="draft-email-body"
                    hasError={!!errors.body}
                    onChange={(html) => setFieldValue("body", html)}
                    placeholder={m.coInvestments_editEmail_placeholder()}
                    readOnly={readOnly}
                    value={values.body}
                  />
                  {errors.body ? (
                    <ValidationErrorText dataTestId="draft-email-body-error">
                      {errors.body}
                    </ValidationErrorText>
                  ) : null}
                </div>
              </div>

              {!autoSave && !readOnly ? (
                <div className="flex justify-end">
                  <ThemedButton
                    dataTestId="draft-email-preview"
                    disabled={!hasBody}
                    onClick={preview.setTrue}
                    type="button"
                    variant="text"
                  >
                    {m.coInvestments_draftEmail_preview()}
                  </ThemedButton>
                </div>
              ) : null}

              <div
                className={cnTwMerge(isScheduled && "opacity-60")}
                inert={isScheduled}
              >
                <EmailAccessSection
                  alreadyHasAccess={alreadyHasAccess}
                  disabled={readOnly}
                  grants={accessGrants}
                  nonUsers={nonUsers}
                  onChangeGrantRole={onChangeGrantRole}
                  onToggleGrant={onToggleGrant}
                  roleOptions={roleOptions}
                />
              </div>

              {!autoSave && !readOnly ? (
                <div className="flex flex-col gap-2 border-t border-border/primary pt-4">
                  <div className="flex items-end gap-3">
                    <ThemedDateField
                      label={m.coInvestments_draftEmail_sendDate_label()}
                      name="scheduleDate"
                      onChange={(date) => setFieldValue("scheduleDate", date)}
                      onlyFuture
                      placeholder={m.coInvestments_draftEmail_sendDate_placeholder()}
                      value={values.scheduleDate}
                    />
                    <ScheduledTimeInput
                      id="draft-email-time"
                      onChange={(time) => setFieldValue("scheduleTime", time)}
                      value={values.scheduleTime}
                    />
                    {isScheduled ? (
                      <ThemedButton
                        dataTestId="draft-email-cancel-schedule"
                        disabled={isCancellingSchedule}
                        isLoading={isCancellingSchedule}
                        onClick={handleCancelSchedule}
                        type="button"
                        variant="text"
                      >
                        {m.coInvestments_draftEmail_cancelSchedule()}
                      </ThemedButton>
                    ) : null}
                    {!isScheduled && hasSchedule ? (
                      <ThemedButton
                        dataTestId="draft-email-clear-schedule"
                        onClick={clearSchedule}
                        type="button"
                        variant="text"
                      >
                        {m.coInvestments_draftEmail_clearSchedule()}
                      </ThemedButton>
                    ) : null}
                  </div>
                  {errors.scheduleDate ? (
                    <ValidationErrorText dataTestId="draft-email-schedule-error">
                      {errors.scheduleDate as string}
                    </ValidationErrorText>
                  ) : null}
                </div>
              ) : null}
            </div>

            {autoSave && isScheduled && !readOnly ? (
              <Notice
                action={
                  <ThemedButton
                    dataTestId="draft-email-cancel-schedule"
                    disabled={isCancellingSchedule}
                    isLoading={isCancellingSchedule}
                    onClick={handleCancelSchedule}
                    type="button"
                    variant="text"
                  >
                    {m.coInvestments_draftEmail_cancelSchedule()}
                  </ThemedButton>
                }
                className="pb-0"
                content={m.coInvestments_draftEmail_scheduledLockNotice()}
                dataTestId="draft-email-scheduled-lock"
                type={NoticeType.INFO}
              />
            ) : null}

            {autoSave && !readOnly && emailId && !isScheduled ? (
              <div className="mt-4 flex min-h-5 justify-end">
                <DraftAutoSave
                  dealId={dealId}
                  defaultRoleId={defaultRoleId}
                  emailId={emailId}
                  knownUserIds={knownUserIds}
                  onSaved={onSaved}
                />
              </div>
            ) : null}

            {!autoSave && !readOnly ? (
              <div className="mt-6 flex flex-col-reverse gap-4 sm:mt-8 sm:flex-row sm:items-center sm:justify-between sm:gap-0">
                <ThemedButton
                  dataTestId="draft-email-cancel"
                  disabled={isSubmitting}
                  onClick={onCancel}
                  type="button"
                  variant="secondary"
                >
                  {m.btn_cancel()}
                </ThemedButton>
                <div className="flex flex-col-reverse gap-3 sm:flex-row sm:gap-4">
                  <ThemedButton
                    dataTestId="draft-email-schedule-send"
                    disabled={isSubmitting}
                    icon={<Calendar01Icon className="size-4" />}
                    iconPosition="left"
                    isLoading={submittingMode === "schedule"}
                    onClick={() => triggerSubmit("schedule")}
                    type="button"
                    variant="secondary"
                  >
                    {m.coInvestments_draftEmail_scheduleSend()}
                  </ThemedButton>
                  <ThemedButton
                    dataTestId="draft-email-save-draft"
                    disabled={isSubmitting || hasSchedule}
                    isLoading={submittingMode === "draft"}
                    onClick={() => triggerSubmit("draft")}
                    type="button"
                    variant="secondary"
                  >
                    {m.coInvestments_draftEmail_saveDraft()}
                  </ThemedButton>
                  <ThemedButton
                    dataTestId="draft-email-send"
                    disabled={isSubmitting || !isSendable}
                    isLoading={submittingMode === "send"}
                    onClick={() => triggerSubmit("send")}
                    type="button"
                    variant="primary"
                  >
                    {m.coInvestments_draftEmail_send()}
                  </ThemedButton>
                </div>
              </div>
            ) : null}

            {!autoSave && !readOnly ? (
              <PreviewEmailModal
                body={values.body}
                dealId={dealId}
                dealShortlistItemId={dealShortlistItemId}
                isOpen={preview.isOpen}
                onClose={preview.setFalse}
              />
            ) : null}
          </Form>
        );
      }}
    </Formik>
  );
}
